import { useTranslation } from 'react-i18next'
import { Card, CodeViewer, EmptyState, KeypadHint, Skeleton } from '@/ds'
import type { KeypadHintItem } from '@/ds'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { dayMonthTime } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { accesosService } from '@/services/accesos'
import type { AccessCode } from '@/services/accesos'
import { SectionError } from '../Estancia/sections/SectionError'
import { AccessBack, EmergencyLine, emergencyPhone, placeName } from './shared'

/**
 * Viñetas de teclado de los pasos de la propiedad (AccesosDetalle.dc.html `code`): el primero enciende el
 * teclado (barra), el central marca las cifras del código y el último confirma (barra en verde).
 */
function keypadItems(code: AccessCode): KeypadHintItem[] {
  const digits = [...code.value].map(Number).filter((n) => n >= 1 && n <= 9)
  const last = code.howTo.length - 1
  return code.howTo.map((text, i) => ({
    label: `${i + 1} · ${text}`,
    ...(i === 0 && last > 0 ? { bar: true } : i === last ? { bar: true, tone: 'success' as const } : { keys: digits }),
  }))
}

/**
 * 8.4 · Código de la puerta (AccesosDetalle.dc.html `code`, Bloque 4 §4c). La API actual no devuelve
 * códigos de teclado: solo con mock (docs/backend/accesos.md). Visible solo con la llave activa.
 */
export function Codigo() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const stay = useLoadedStay()
  const [key, reload] = useAsync(`key:${stay.guestCode}|${stay.phase}`, (signal) => accesosService.getKey(stay, signal))
  const access = key.kind === 'ready' && key.value.state === 'active' ? key.value.accesses.find((a) => a.code) : undefined
  const code = access?.code
  return (
    <main className="page">
      <div className="accesos__head">
        <AccessBack />
        <h1>{t('v2.accesos.codigo.titulo')}</h1>
        <p className="page__lead">{t('v2.accesos.codigo.lead', { name: placeName(stay) })}</p>
      </div>
      {key.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={200} radius={24} />
        </div>
      )}
      {key.kind === 'error' && <SectionError code={key.code} onRetry={reload} />}
      {key.kind === 'ready' && !code && (
        <EmptyState icon="key" title={t('v2.accesos.codigo.sinCodigo')} headingLevel={2}>
          {t('v2.accesos.codigo.sinCodigoTexto')}
        </EmptyState>
      )}
      {access && code && (
        <>
          <Card className="accesos__box">
            <CodeViewer
              standalone
              code={code.value}
              label={access.name}
              {...(code.validFrom && code.validUntil ? { validity: t('v2.accesos.codigo.validez', { from: dayMonthTime(code.validFrom, lang), to: dayMonthTime(code.validUntil, lang) }) } : {})}
            />
          </Card>
          {code.howTo.length > 0 && (
            <section className="accesos__section" aria-labelledby="codigo-como">
              <h2 id="codigo-como" className="accesos__h2">
                {t('v2.accesos.codigo.comoIntroducirlo')}
              </h2>
              <KeypadHint items={keypadItems(code)} />
              {code.troubleshooting && <p className="page__lead">{code.troubleshooting}</p>}
            </section>
          )}
        </>
      )}
      <EmergencyLine phone={emergencyPhone(stay)} />
    </main>
  )
}
